/// <reference types="vite/client" />
import { hc } from 'hono/client';
import type { ClientResponse } from 'hono/client';
import type { AppType } from '@siliconx/api/types';
import { createAuthFetch } from '@siliconx/shared/auth';
import { client } from './auth';

// Customer has no same-origin /api proxy (unlike apps/operations, which can
// leave this blank in dev) — VITE_API_BASE_URL must be an absolute URL. This
// is the single source of truth for the base; every other file (auth.ts's
// inlined /me fetch, the former per-route API_BASE consts in quotes/*,
// submissions/*, transactions/index.tsx) should read from here, not
// redeclare it. (SIL-320: previously apps/operations used VITE_API_URL —
// customer keeps its own established VITE_API_BASE_URL name instead of
// copying that mismatched var during the port.)
export const API_BASE =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'http://localhost:3001';

// Token attach + 401-retry via the shared PKCE client (RUF-393/394). `client`
// is configured with tokenKind: 'id' — clientAuthMiddleware validates the
// Cognito ID token (checks `aud`, reads `email`, neither of which the access
// token carries). createAuthFetch is token-kind agnostic (see
// packages/shared/src/auth/fetch.ts), so it works here unmodified.
const authFetch = createAuthFetch(client);

export { authFetch };

/** Hono RPC typed client, mirroring apps/operations/src/lib/api.ts. */
export const api = hc<AppType>(API_BASE, { fetch: authFetch });

/**
 * File/blob downloads (CSV/PDF exports) — streams an authenticated response
 * into a client-side blob download. Not modeled through `hc` (Hono RPC
 * assumes a JSON response); goes through the same authFetch pipeline for
 * the 401-retry behavior.
 */
export async function apiDownload(path: string, filename: string): Promise<void> {
  const res = await authFetch(`${API_BASE}${path}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Throw a uniform Error for a non-ok hc client response, matching the old
 * apiGet/Post error shape. `error` is inconsistently either a plain string
 * (`{ error: 'message' }`, the majority shape across apps/api/src/routes) or
 * an object (`{ error: { code, message } }`, used by a minority of routes,
 * e.g. lot-not-found) — read whichever is present rather than assuming the
 * object shape, or a plain-string error silently degrades to the generic
 * `HTTP <status>` fallback and the real message never reaches the UI.
 */
export async function throwIfNotOk(res: Response): Promise<void> {
  if (res.ok) return;
  const body = await res.json().catch(() => ({}));
  const error = (body as { error?: string | { message?: string } })?.error;
  const message = (typeof error === 'string' ? error : error?.message) ?? `HTTP ${res.status}`;
  throw Object.assign(new Error(message), { status: res.status, body });
}

/**
 * hc's typed responses union the success body with every non-2xx
 * error-branch body (`{ error: {...} }` literals). Accessing `.json()`
 * before narrowing on `res.ok` widens the result to that union and loses
 * field access on success-only fields. `await throwIfNotOk(res)` does NOT
 * narrow (the throw happens inside a separate function call, invisible to
 * TS control-flow analysis) — callers with a multi-branch response type
 * must narrow inline instead:
 *
 *   const res = await api.api.client.foo.$get();
 *   if (!res.ok) {
 *     await throwIfNotOk(res);
 *     throw new Error(`HTTP ${res.status}`);
 *   }
 *   return res.json();
 *
 * `unwrap` below projects the success branch out of the union *by
 * construction* instead: its return type is derived from the response's
 * status literal, so a caller that only ever reaches the `return` gets a
 * narrowed type with no inline `if (!res.ok)` of their own. SIL-716.
 */

/**
 * Success statuses actually returned by routes/client/*.ts (checked
 * 2026-08-19: every `c.json(x, N)` call site there uses 200 (implicit
 * default) or 201 — no route returns 202/204/other 2xx). A route added later
 * that returns a different 2xx will make `OkBody` resolve to `never` for
 * that endpoint's success branch, which is a loud compile error at the
 * call site, not a silent miss.
 */
type OkBody<R> = R extends ClientResponse<infer T, infer S, 'json'>
  ? S extends 200 | 201
    ? T
    : never
  : never;

/**
 * Await + narrow a `hc<AppType>` response in one call: throws (via
 * `throwIfNotOk`) on a non-ok response, otherwise returns the parsed,
 * success-narrowed JSON body. Replaces the `if (!res.ok) { ... }`
 * boilerplate documented above at call sites that don't need to branch on
 * the error themselves.
 *
 *   return unwrap(await api.api.client.foo.$get());
 *
 * Deliberately excludes `apiDownload` responses (streamed blobs, not `hc`
 * JSON) — `unwrap` only accepts `ClientResponse<..., 'json'>`.
 */
export async function unwrap<R extends ClientResponse<any, any, any>>(res: R): Promise<OkBody<R>> {
  if (!res.ok) {
    await throwIfNotOk(res as unknown as Response);
    throw new Error(`HTTP ${res.status}`);
  }
  return res.json() as Promise<OkBody<R>>;
}
